<template>
  <div class="timer-single-display" :class="displayClass">
    <template v-if="isCustomTheme">
      <div class="single-custom-layout">
        <div class="single-body__mask" aria-hidden="true">
          <img class="single-body__mask-image" :src="customMaskSrc" alt="" />
        </div>

        <div class="single-custom-overlay" :class="{ 'single-custom-overlay--debug': layoutDebug }" :style="customStyleVars">
          <div v-if="layoutDebug" class="custom-layout-debug-chip">
            {{ customLayoutProfile.key }} {{ customLayoutProfile.width }}x{{ customLayoutProfile.height }}
          </div>

          <div v-if="athlete && showCustomCountryCode" class="custom-slot custom-slot--flag" :class="{ 'custom-slot--debug': layoutDebug }" :style="getCustomSlotStyle('flag')" data-slot="flag">
            <span class="custom-slot__label">flag</span>
            <div class="custom-flag-frame">
              <span class="custom-country-code">{{ customCountryCode }}</span>
            </div>
          </div>

          <div class="custom-slot custom-slot--phase" :class="{ 'custom-slot--debug': layoutDebug }" :style="getCustomSlotStyle('phase')" data-slot="phase">
            <span class="custom-slot__label">phase</span>
            <span class="custom-phase-name" :title="phaseLabel || 'Stage unavailable'">{{ phaseLabel || '--' }}</span>
          </div>

          <div v-if="athlete" class="custom-slot custom-slot--name" :class="{ 'custom-slot--debug': layoutDebug }" :style="getCustomSlotStyle('name')" data-slot="name">
            <span class="custom-slot__label">name</span>
            <span class="athlete-name custom-athlete-name" :title="displayAthleteName">
              <span class="custom-athlete-name__text">{{ displayAthleteName }}</span>
              <span class="custom-athlete-name__dot">.</span>
            </span>
          </div>

          <div v-if="showCustomStatusControls" class="custom-slot custom-slot--status" :class="{ 'custom-slot--debug': layoutDebug }" :style="getCustomSlotStyle('status')" data-slot="status">
            <span class="custom-slot__label">status</span>
            <div class="custom-status-icon" :class="`custom-status-icon--${resolvedStatusIcon}`" :aria-label="stateLabel" :title="stateLabel">
              <template v-if="resolvedStatusIcon === 'pause'">
                <span class="custom-status-icon__pause-bar"></span>
                <span class="custom-status-icon__pause-bar"></span>
              </template>
              <span v-else-if="resolvedStatusIcon === 'play'" class="custom-status-icon__play"></span>
              <span v-else class="custom-status-icon__stop"></span>
            </div>
          </div>

          <div v-if="showCustomPowerIndicator" class="custom-slot custom-slot--power" :class="{ 'custom-slot--debug': layoutDebug }" :style="getCustomSlotStyle('power')" data-slot="power">
            <span class="custom-slot__label">power</span>
            <span class="custom-power-pill" :class="customPowerClass">{{ customPowerLabel }}</span>
          </div>

          <div class="custom-slot custom-slot--timer" :class="{ 'custom-slot--debug': layoutDebug }" :style="getCustomSlotStyle('timer')" data-slot="timer">
            <span class="custom-slot__label">timer</span>
            <div class="big-timer custom-big-timer" :class="{ 'big-timer--overtime': isOvertime }">
              <span class="custom-big-timer__main">{{ timerDisplayParts.main }}</span>
              <span class="custom-big-timer__fraction">{{ timerDisplayParts.fraction }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
    <div v-if="athlete" class="single-header">
      <div class="single-header__info">
        <div class="flag" v-if="athlete.flagUrl"><img class="flag-img" :src="athlete.flagUrl" alt="flag" /></div>
        <div class="flag" v-else-if="athlete.countryCode">
          <flag class="flag-svg" :iso="athlete.countryCode.toLowerCase()" />
        </div>
        <span class="athlete-name single-athlete-name" :title="displayAthleteName">{{ displayAthleteName }}</span>
      </div>
      <div class="status-pill" :class="stateClass">{{ stateLabel }}</div>
    </div>

    <div class="single-body">
      <div v-if="isCustomTheme" class="single-body__mask" aria-hidden="true">
        <img class="single-body__mask-image" :src="customMaskSrc" alt="" />
      </div>

      <div class="single-body__content">
        <div class="big-timer" :class="{ 'big-timer--overtime': isOvertime }">{{ remainingDisplay }}</div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import timerSingleMask from '@/assets/timer_mask/timerSingle.png';
import gShockSingleMask from '@/assets/timer_mask/G-SHOCK_minimal.png';
import {
  isCustomTimerTheme,
  normalizeTimerTheme,
} from '@/constants/timerThemes';
import {
  resolveCustomTimerLayoutProfile,
  resolveCustomTimerSlotStyles,
  resolveCustomTimerStyleVars,
} from '@/constants/timerLayoutPresets';

const CUSTOM_SINGLE_MASKS = {
  custom: timerSingleMask,
  custom_2: gShockSingleMask,
};

export default {
  name: 'TimerSingleDisplay',
  data() {
    return {
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth || 1920 : 1920,
      viewportHeight: typeof window !== 'undefined' ? window.innerHeight || 1080 : 1080,
    };
  },
  props: {
    athlete: {
      type: Object,
      default: null,
    },
    phaseLabel: {
      type: String,
      default: '',
    },
    remainingDisplay: {
      type: String,
      default: '00:00',
    },
    remainingSeconds: {
      type: Number,
      default: null,
    },
    stateValue: {
      type: String,
      default: 'idle',
    },
    isRecording: {
      type: Boolean,
      default: false,
    },
    isOvertime: {
      type: Boolean,
      default: false,
    },
    stateLabel: {
      type: String,
      default: 'IDLE',
    },
    stateClass: {
      type: [Object, Array, String],
      default: () => ({}),
    },
    theme: {
      type: String,
      default: 'dark',
    },
    layoutDebug: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    normalizedTheme() {
      return normalizeTimerTheme(this.theme);
    },
    displayClass() {
      return {
        'timer-single-display--light': this.normalizedTheme === 'light',
        'timer-single-display--custom': this.isCustomTheme,
        'timer-single-display--custom-2': this.normalizedTheme === 'custom_2',
      };
    },
    isCustomTheme() {
      return isCustomTimerTheme(this.normalizedTheme);
    },
    isCustomThemeVariantTwo() {
      return this.normalizedTheme === 'custom_2';
    },
    customMaskSrc() {
      return CUSTOM_SINGLE_MASKS[this.normalizedTheme] || CUSTOM_SINGLE_MASKS.custom;
    },
    showCustomCountryCode() {
      return !this.isCustomThemeVariantTwo;
    },
    showCustomStatusControls() {
      return !this.isCustomThemeVariantTwo;
    },
    showCustomPowerIndicator() {
      return !this.isCustomThemeVariantTwo;
    },
    customCountryCode() {
      const code = String(this.athlete?.countryCode || '').trim().toUpperCase();
      return code || '--';
    },
    displayAthleteName() {
      return this.formatAthleteName(this.athlete);
    },
    resolvedStatusIcon() {
      const state = String(this.stateValue || '').toLowerCase();
      if (state === 'paused') return 'pause';
      if (state === 'running') return 'play';
      return 'stop';
    },
    customPowerLabel() {
      return this.isRecording ? 'ON' : 'OFF';
    },
    customPowerClass() {
      return this.isRecording
        ? 'custom-power-pill--on'
        : 'custom-power-pill--off';
    },
    timerDisplayParts() {
      const numericValue = Number(this.remainingSeconds);
      if (!Number.isFinite(numericValue)) {
        return {
          main: this.remainingDisplay || '00:00',
          fraction: '00',
        };
      }

      const absoluteValue = Math.max(0, Math.abs(numericValue));
      const wholeSeconds = Math.floor(absoluteValue);
      const minutes = Math.floor(wholeSeconds / 60).toString().padStart(2, '0');
      const seconds = (wholeSeconds % 60).toString().padStart(2, '0');
      const centiseconds = Math.min(99, Math.floor((absoluteValue - wholeSeconds) * 100 + 0.0001))
        .toString()
        .padStart(2, '0');

      return {
        main: `${minutes}:${seconds}`,
        fraction: centiseconds,
      };
    },
    customSlotStyles() {
      return resolveCustomTimerSlotStyles('single', this.normalizedTheme, this.viewportWidth, this.viewportHeight);
    },
    customStyleVars() {
      return resolveCustomTimerStyleVars('single', this.normalizedTheme, this.viewportWidth, this.viewportHeight);
    },
    customLayoutProfile() {
      return resolveCustomTimerLayoutProfile(this.viewportWidth, this.viewportHeight);
    },
  },
  mounted() {
    this.updateViewportWidth();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateViewportWidth, { passive: true });
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateViewportWidth);
    }
  },
  methods: {
    updateViewportWidth() {
      if (typeof window === 'undefined') return;
      this.viewportWidth = window.innerWidth || 1920;
      this.viewportHeight = window.innerHeight || 1080;
    },
    getCustomSlotStyle(slotName) {
      return this.customSlotStyles?.[slotName] || null;
    },
    formatAthleteName(athlete) {
      const firstName = String(athlete?.firstName || '').trim();
      const lastName = String(athlete?.lastName || '').trim();
      if (lastName || firstName) {
        return [firstName, lastName].filter(Boolean).join(' ').trim();
      }

      const fullName = String(athlete?.name || '').trim();
      if (!fullName) return '--';

      const parts = fullName.split(/\s+/).filter(Boolean);
      if (parts.length <= 1) return fullName;

      return parts.join(' ');
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@700;800;900&display=swap');

@font-face {
  font-family: 'TimerDigits';
  src: url('../../assets/fonts/LCDAT&TPhoneTimeDate.ttf') format('truetype');
  font-weight: 400;
  font-style: normal;
}

.timer-single-display {
  width: 100vw;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding-top: clamp(72px, 8vw, 106px);
}

.single-header {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9;
  width: 100vw;
  min-height: clamp(72px, 8vw, 106px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: clamp(8px, 1.4vw, 14px) clamp(10px, 2.4vw, 24px);
  box-sizing: border-box;
  background: transparent;
}

.single-header__info {
  display: flex;
  align-items: center;
  gap: clamp(8px, 1.4vw, 16px);
  min-width: 0;
  flex: 1;
}

.single-athlete-name {
  max-width: min(78vw, 1200px);
}

.single-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  position: relative;
  overflow: hidden;
}

.single-body__mask {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.single-body__mask-image {
  width: min(100vw, 1920px);
  max-width: 100%;
  height: auto;
  object-fit: contain;
}

.single-custom-layout {
  position: relative;
  width: 100vw;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.single-custom-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
}

.custom-slot {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--custom-slot-radius, 14px);
  padding: var(--custom-slot-padding, 10px);
}

.custom-slot__label {
  position: absolute;
  top: -12px;
  left: 12px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.96);
  color: #eff6ff;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  display: none;
}

.single-custom-overlay--debug .custom-slot {
  outline: 2px dashed rgba(59, 130, 246, 0.95);
  background: rgba(15, 23, 42, 0.18);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
}

.single-custom-overlay--debug .custom-slot__label {
  display: inline-flex;
}

.custom-layout-debug-chip {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.9);
  color: #eff6ff;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.custom-flag-frame {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-country-code {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-align: center;
  font-size: var(--custom-country-font-size, clamp(1.8rem, 3.2vw, 3.4rem));
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #ffffff;
}

.custom-phase-name {
  width: 100%;
  font-size: var(--custom-phase-font-size, clamp(1.6rem, 2.7vw, 2.8rem));
  line-height: 1.05;
  font-weight: 800;
  letter-spacing: 0.03em;
  color: #000000;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.custom-flag {
  width: min(100%, 180px);
}

.custom-athlete-name {
  width: 100%;
  font-size: clamp(1.3rem, 2.4vw, 2.5rem);
  text-align: left;
}

.custom-status-pill {
  width: 100%;
  justify-content: center;
  display: inline-flex;
  align-items: center;
  font-size: clamp(0.8rem, 1.2vw, 1.05rem);
}

.custom-status-icon {
  width: 100%;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--custom-status-gap, clamp(8px, 1vw, 14px));
  background: transparent;
}

.custom-status-icon__pause-bar {
  width: var(--custom-status-pause-width, clamp(10px, 0.9vw, 16px));
  height: var(--custom-status-pause-height, clamp(44px, 5.2vw, 72px));
  border-radius: 999px;
  background: #dc2626;
}

.custom-status-icon__play {
  width: 0;
  height: 0;
  border-top: var(--custom-status-play-top, clamp(22px, 2vw, 30px)) solid transparent;
  border-bottom: var(--custom-status-play-bottom, clamp(22px, 2vw, 30px)) solid transparent;
  border-left: var(--custom-status-play-left, clamp(34px, 3vw, 48px)) solid #16a34a;
  margin-left: var(--custom-status-play-offset, clamp(6px, 0.5vw, 10px));
}

.custom-status-icon__stop {
  width: var(--custom-status-stop-size, clamp(40px, 3.2vw, 58px));
  height: var(--custom-status-stop-size, clamp(40px, 3.2vw, 58px));
  background: #dc2626;
}

.custom-power-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: var(--custom-power-radius, 18px);
  font-size: var(--custom-power-font-size, clamp(1.8rem, 3vw, 3rem));
  font-weight: 900;
  letter-spacing: 0.08em;
  color: #ffffff;
}

.custom-power-pill--on {
  color: #16a34a;
}

.custom-power-pill--off {
  color: #dc2626;
}

.custom-big-timer {
  width: 100%;
  height: 100%;
  max-width: 100%;
  font-size: var(--custom-main-timer-font-size, clamp(6rem, 21vw, 18rem));
  font-weight: 100;
  color: #000;
  padding: 0;
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  column-gap: 0.12em;
  font-family: 'TimerDigits', sans-serif;
  letter-spacing: 0.01em;
  line-height: 1;
  overflow: hidden;
  white-space: nowrap;
  box-sizing: border-box;
}

.custom-big-timer__main {
  line-height: 1;
  display: block;
  transform: translateY(0.02em);
}

.custom-big-timer__fraction {
  font-size: 0.34em;
  line-height: 1;
  display: block;
  letter-spacing: 0.04em;
  transform: translateY(0.02em);
}

.single-body__content {
  position: relative;
  z-index: 2;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
}

.big-timer {
  font-size: clamp(7rem, 22vw, 22rem);
  font-weight: 900;
  color: currentColor;
  text-align: center;
  letter-spacing: clamp(1px, 1.2vw, 3px);
  line-height: 0.98;
  max-width: 100%;
  padding: 0 4vw;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  min-width: 6ch;
}

.timer-single-display--custom .custom-big-timer {
  font-size: var(--custom-main-timer-font-size, clamp(6rem, 21vw, 18rem));
  font-weight: 100;
  color: #000;
}

.timer-single-display--custom .custom-big-timer.big-timer--overtime {
  color: #dc2626;
}

.big-timer--overtime {
  color: #ef4444;
}

.athlete-name {
  font-weight: 800;
  font-size: clamp(1.9rem, 3.6vw, 2.8rem);
  color: currentColor;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.timer-single-display--custom .custom-athlete-name {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  font-size: var(--custom-name-font-size, clamp(3.3rem, 4.4vw, 4.5rem));
  font-weight: 800;
  color: #000000;
  text-align: left;
  font-family: 'Orbitron', sans-serif;
  text-transform: uppercase;
  flex: 0 1 auto;
  display: flex;
  align-items: baseline;
  gap: var(--custom-name-gap, 0.18em);
  box-sizing: border-box;
}

.timer-single-display--custom .custom-athlete-name__text {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: clip;
  white-space: nowrap;
}

.timer-single-display--custom .custom-athlete-name__dot {
  flex: 0 0 auto;
  color: currentColor;
  line-height: 1;
}

.flag {
  display: flex;
  align-items: center;
  justify-content: center;
  width: clamp(86px, 10vw, 150px);
  aspect-ratio: 16 / 9;
  flex: 0 0 auto;
}

.status-pill {
  padding: 10px 14px;
  border-radius: 12px;
  font-weight: 800;
  text-transform: uppercase;
  font-size: clamp(0.95rem, 2.2vw, 1.2rem);
  background: #1e293b;
  color: #f8fafc;
}

.status-pill--running { background: #16a34a; color: #f8fafc; }
.status-pill--paused { background: #d97706; color: #f8fafc; }
.status-pill--finished { background: #475569; color: #f8fafc; }
.status-pill--idle { background: #334155; color: #f8fafc; }
.status-pill--waiting { background: #2563eb; color: #f8fafc; }

.flag-img {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
  border-radius: 8px;
}

:deep(.flag-svg),
:deep(flag) {
  width: 100% !important;
  height: 100% !important;
  display: block;
}

:deep(.flag-svg svg),
:deep(.flag-svg img),
:deep(flag svg),
:deep(flag img) {
  width: 100% !important;
  height: 100% !important;
  display: block;
  object-fit: cover;
}

.timer-single-display--light .single-header,
.timer-single-display--light .single-body {
  background: transparent;
  color: #0f172a;
}

.timer-single-display--light .status-pill {
  color: #ffffff;
}

.timer-single-display--light .big-timer--overtime {
  color: #dc2626;
}

.timer-single-display--custom {
  color: #f8fafc;
  padding-top: 0;
}

.timer-single-display--custom .single-body__mask-image {
  width: 100vw;
  height: 100vh;
  max-width: none;
  object-fit: cover;
  object-position: center center;
}

.timer-single-display--custom-2 .single-custom-layout {
  width: 100vw;
  min-height: 100vh;
}

.timer-single-display--custom-2 .single-body__mask {
  inset: 0;
}

.timer-single-display--custom-2 .single-body__mask-image {
  width: 100vw;
  height: 100vh;
  max-width: none;
  object-fit: cover;
  object-position: center center;
}

.timer-single-display--custom-2 .custom-slot--name {
  overflow: hidden;
}

.timer-single-display--custom-2 .custom-athlete-name {
  max-width: 100%;
}

.timer-single-display--custom-2 .custom-athlete-name__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.timer-single-display--custom-2 .custom-athlete-name__dot {
  display: none;
}

@media (max-width: 1024px) {
  .big-timer {
    font-size: clamp(6.8rem, 20vw, 18rem);
    letter-spacing: clamp(1px, 1vw, 2px);
    padding: 0 5vw;
  }

  .single-header {
    min-height: clamp(68px, 9vw, 94px);
    padding-left: clamp(10px, 3vw, 16px);
    padding-right: clamp(10px, 3vw, 16px);
  }
}

@media (max-width: 768px) {
  .big-timer {
    font-size: clamp(5.8rem, 20vw, 12.5rem);
    letter-spacing: clamp(0.8px, 0.9vw, 1.6px);
    padding: 0 5vw;
  }

  .single-header {
    padding: 9px 12px;
  }

  .athlete-name {
    font-size: clamp(1.3rem, 4.4vw, 1.9rem);
  }

  .flag {
    width: clamp(62px, 16vw, 98px);
  }

  .status-pill {
    font-size: clamp(0.9rem, 3vw, 1.05rem);
    padding: 7px 10px;
  }
}

@media (max-width: 540px) {
  .big-timer {
    font-size: clamp(4.8rem, 22vw, 10rem);
    letter-spacing: clamp(0.6px, 0.8vw, 1.2px);
    padding: 0 6vw;
  }

  .athlete-name {
    font-size: clamp(1.15rem, 4.8vw, 1.6rem);
  }

  .status-pill {
    padding: 6px 8px;
    font-size: clamp(0.72rem, 2.8vw, 0.92rem);
  }

  .custom-slot__label {
    font-size: 0.62rem;
  }
}
</style>